/* global archiveUrl statuses */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';

import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import CircleCheck from 'Assets/icons/circle-check.svg';
import CircleInfo from 'Assets/icons/circle-info.svg';
import Circle from 'Assets/icons/circle.svg';
import FileExport from 'Assets/icons/file-export.svg';
import Hourglass from 'Assets/icons/hourglass.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function BudgetByVenuesArchiveIndex(props) {
    const [reports, setReports] = useState([]);
    const [pagination, setPagination] = useState({
        perPage: 10,
        page: 1,
        lastPage: 1,
        to: 1,
        from: 1,
        total: 1,
    });

    const toast = useToast();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [pagination.page, pagination.perPage]);

    const combineUrl = (url) => {
        const parsedUrl = new URL(url);
        parsedUrl.searchParams.set('page', pagination.page ?? 1);
        parsedUrl.searchParams.set('per_page', pagination.perPage ?? 10);

        return parsedUrl.toString();
    };

    const fetchData = () => {
        const fetchUrl = combineUrl(props.fetchUrl);

        axios
            .get(fetchUrl)
            .then((response) => {
                const { data, links, meta } = response.data;
                setReports(data);

                const paginationData = JSON.parse(JSON.stringify(pagination));
                paginationData.page = meta.current_page;
                paginationData.from = meta.from;
                paginationData.total = meta.total;
                paginationData.to = meta.to;
                paginationData.perPage = meta.per_page;
                paginationData.lastPage = meta.last_page;

                setPagination(paginationData);
            })
            .catch((error) => {
                toast.error($t('Something went wrong'));
                console.log(error.response.data);
            });
    };

    const setPaginationPerPage = (val) => {
        const paginationData = JSON.parse(JSON.stringify(pagination));
        paginationData.perPage = val;
        setPagination(paginationData);
    };

    const setPaginationPage = (val) => {
        const paginationData = JSON.parse(JSON.stringify(pagination));
        paginationData.page = val;
        setPagination(paginationData);
    };

    const deleteReport = (report) => {
        if (!report.canBeDeleted) {
            return false;
        }

        if (confirm($t('Are you sure you want to delete this report?'))) {
            axios
                .delete(report.deleteUrl)
                .then((response) => {
                    toast.success($t('Successfully deleted'));
                    fetchData();
                })
                .catch((error) => {
                    const message = error.response?.data?.message || $t('Something went wrong');
                    toast.error($t('Something went wrong'));
                });
        }
    };

    return (
        <div className="tw-py-10">
            <div className="tw-mb-3">
                <div className="table__pagination-info tw-mb-2">
                    <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                    <TablePagination
                        lastPage={pagination.lastPage}
                        showPerPage={true}
                        perPage={pagination.perPage}
                        currentPage={pagination.page}
                        perPageOptions={[10, 25, 50]}
                        onPerPageChange={(val) => {
                            setPaginationPerPage(val);
                        }}
                        pageChange={(data) => {
                            setPaginationPage(+data.selected + 1);
                        }}
                    />
                </div>
                <div className={classNames('inbud-table_wrapper', 'tw-overflow-auto', 'tw-relative')}>
                    <table className="inbud-table tw-w-full">
                        <thead>
                            <tr>
                                <th className="!tw-align-middle tw-border-r-2 tw-border-b-2 tw-w-1/6">{$t('Date')}</th>
                                <th className="!tw-align-middle tw-border-r-2 tw-border-b-2 tw-w-1/6">{$t('Status')}</th>
                                <th className="!tw-align-middle tw-border-r-2 tw-border-b-2 tw-w-1/6">{$t('Workforce')}</th>
                                <th className="!tw-align-middle tw-border-r-2 tw-border-b-2 tw-w-1/6">{$t('Format')}</th>
                                <th className="!tw-align-middle tw-border-r-2 tw-border-b-2 tw-w-1/6"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((item, index) => {
                                return (
                                    <tr key={item.id}>
                                        <td>{item.date}</td>
                                        <td>{item.statusLabel}</td>
                                        <td>
                                            {item.options.include_workforce !== undefined && item.options.include_workforce === '1' && (
                                                <CircleCheck className="tw-h-3 tw-w-3 tw-text-green" />
                                            )}
                                            {(item.options.include_workforce === undefined || item.options.include_workforce !== '1') && (
                                                <Circle className="tw-h-3 tw-w-3 tw-text-charcoal" />
                                            )}
                                        </td>
                                        <td>{item.format}</td>
                                        <td>
                                            <div className="tw-flex tw-justify-start tw-items-center">
                                                <div className="tw-cursor-pointer tw-mr-3" onClick={() => props.processClickDelete(props.index)}>
                                                    {item.status === props.statuses.completed && (
                                                        <Tooltip placement="top" allowTooltip={true} tooltipContent={$t('Download')} tooltipType="text">
                                                            <a className="!tw-text-green" href={item.downloadUrl}>
                                                                <FileExport className="tw-h-3 tw-w-3" />
                                                            </a>
                                                        </Tooltip>
                                                    )}
                                                    {item.status === props.statuses.failed && (
                                                        <Tooltip placement="top" allowTooltip={true} tooltipContent={$t('Failed')} tooltipType="text">
                                                            <a className="!tw-text-red">
                                                                <CircleInfo className="tw-h-3 tw-w-3" />
                                                            </a>
                                                        </Tooltip>
                                                    )}
                                                    {!(item.status === props.statuses.failed || item.status === props.statuses.completed) && (
                                                        <Tooltip placement="top" allowTooltip={true} tooltipContent={$t('In Progress')} tooltipType="text">
                                                            <a className="!tw-text-charcoal">
                                                                <Hourglass className="tw-h-3 tw-w-3" />
                                                            </a>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                {!!item.canBeDeleted && (
                                                    <div className="tw-cursor-pointer tw-mr-3" onClick={() => deleteReport(item)}>
                                                        <TrashIcon className="tw-text-red-500 tw-w-3 tw-h-3" />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

if (document.getElementById('budgetByVenuesReportArchive')) {
    var rootEl = document.getElementById('budgetByVenuesReportArchive');
    ReactDOM.render(
        <App>
            <BudgetByVenuesArchiveIndex fetchUrl={archiveUrl} statuses={statuses} />
        </App>,
        rootEl,
    );
}
